import React, { useEffect, useState } from "react"
import { AiFillEye } from "react-icons/ai"
import { FiEye, FiPlusCircle } from "react-icons/fi"
import Spinner from "../../components/Spinner"
import DashboardHeader from "../../components/DashboardHeader"
import EmptyGroup from "../../components/NoData/EmptyGroup"
import { IoArrowForward, IoArrowBack } from "react-icons/io5"
import { Tooltip } from "../../layout/sidebar"
import DeleteGroupModal from "../../components/modals/DeleteGroupModal"
import api, { fetchAll, BASE_URL } from "../../utils/api"
import { SuccessNotification, ErrorNotification } from "../../utils"
import { useHistory } from "react-router-dom"
import { DateRangePickerDropdown } from '../../components/DashboardOverviewFilter'
import defaultAvatar from '../../assets/pic.png'
import { FiSearch } from "react-icons/fi"
import { RxPencil1 } from "react-icons/rx"
import Pagination from "../../components/Pagination"

const GROUP_TYPES = [
  { value: "", label: "All Types" },
  { value: "farmers", label: "Farmers" },
  { value: "processors", label: "Processors" },
  { value: "service_providers", label: "Service Providers" },
]

const Index = () => {
  const history = useHistory()
  const [groups, setGroups] = useState([])
  const [loading, setLoading] = useState(true)
  const [showConfirmModal, setShowConfirmModal] = useState(false)
  const [selected, setSelected] = useState(null)
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [search, setSearch] = useState("")
  const [groupType, setGroupType] = useState("")
  const [count, setCount] = useState(0)
  const [dateRange, setDateRange] = useState({ start: null, end: null })
  const [showSuggestions, setShowSuggestions] = useState(false)
  const inputRef = React.useRef(null)
  const [deleting, setDeleting] = useState(false)

  useEffect(() => {
    fetchGroups()
  }, [])

  const fetchGroups = async () => {
    setLoading(true)
    try {
      const groupsData = await fetchAll('groups/')
      setGroups(groupsData)
      setCount(groupsData.length)
      setLoading(false)
    } catch {
      setLoading(false)
    }
  }

  // --- Suggestions for search ---
  const groupNameSuggestions = Array.from(new Set(groups.map(g => g.group_name || ''))).filter(Boolean)
  const filteredSuggestions = groupNameSuggestions.filter(s => s.toLowerCase().includes(search.toLowerCase()))

  // --- Filtering and Search Logic ---
  const getFilteredGroups = () => {
    let data = groups || []
    if (groupType) {
      data = data.filter(g => g.group_type === groupType)
    }
    if (search) {
      data = data.filter(g =>
        (g.group_name || '').toLowerCase().includes(search.toLowerCase()) ||
        (g.description || '').toLowerCase().includes(search.toLowerCase())
      )
    }
    if (dateRange.start && dateRange.end) {
      data = data.filter(g => {
        const created = new Date(g.created_at)
        // Compare only the date part
        const createdDate = new Date(created.getFullYear(), created.getMonth(), created.getDate())
        const startDate = new Date(dateRange.start.getFullYear(), dateRange.start.getMonth(), dateRange.start.getDate())
        const endDate = new Date(dateRange.end.getFullYear(), dateRange.end.getMonth(), dateRange.end.getDate())
        return createdDate >= startDate && createdDate <= endDate
      })
    }
    return data
  }

  // --- Pagination Logic ---
  const filteredGroups = getFilteredGroups()
  const totalPages = Math.ceil(filteredGroups.length / pageSize)
  const paginatedGroups = filteredGroups.slice((currentPage - 1) * pageSize, currentPage * pageSize)

  useEffect(() => {
    setCount(totalPages)
    if (currentPage > totalPages && totalPages > 0) {
      setCurrentPage(1)
    }
  }, [filteredGroups.length, pageSize])

  // --- Pagination Handlers ---
  const handlePageChange = (page) => {
    setCurrentPage(page)
  }

  const resetFilters = () => {
    setSearch("")
    setGroupType("")
    setDateRange({ start: null, end: null })
    setCurrentPage(1)
  }

  const handleDelete = (id) => {
    let token = localStorage.getItem("token")
    setDeleting(true)
    fetch(`${BASE_URL}groups/${id}/`, {
      method: "DELETE",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    })
      .then(res => {
        if (res.ok) {
          setDeleting(false)
          setLoading(false)
          fetchGroups()
          SuccessNotification("Group deleted successfully")
          setShowConfirmModal(false)
        } else {
          setDeleting(false)
          setLoading(false)
          ErrorNotification("Failed to delete group")
        }
      })
      .catch(() => {
        setDeleting(false)
        setLoading(false)
        ErrorNotification("Failed to delete group")
      })
  }

  const handleRowClick = (row) => {
    history.push("/admin/groupDetail", { state: row })
  }

  const handleNewGroup = () => {
    history.push("/admin/NewGroup")
  }

  const truncate = (str, n) => {
    return str.length > n ? str.substr(0, n - 1) + "..." : str
  }

  return (
    <>
      {showConfirmModal && (
        <DeleteGroupModal
          setShow={setShowConfirmModal}
          show={showConfirmModal}
          handleDelete={() => handleDelete(selected)}
          deleting={deleting}
        />
      )}
      <div className="w-full lg:px-10 px-2 h-full py-10">
        <div className="w-full space-y-6">
          <div className="flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between md:flex-row md:items-center md:justify-between mb-4">
            <DashboardHeader label="Groups" />
            <button
              onClick={handleNewGroup}
              className="bg-yellowcolor hover:bg-yellowcolor/80 text-black px-6 flex flex-row items-center gap-2 py-2 capitalize rounded-md"
            >
              <FiPlusCircle size={20} />
              Create New Group
            </button>
          </div>
          {/* Search and Filters Bar */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between md:gap-4 gap-4 w-full">
            {/* Search with suggestions */}
            <div className="flex-1 flex flex-row gap-2 w-full md:w-1/2 relative">
              <input
                type="text"
                placeholder="Search group"
                className="w-full bg-white dark:bg-dark border dark:border-border-dark dark:text-white rounded-md py-2 font-light px-9 focus:outline-none focus:ring-2 focus:ring-yellowcolor focus:border-transparent placeholder-gray-400"
                value={search}
                onChange={e => { setSearch(e.target.value); setCurrentPage(1); setShowSuggestions(true) }}
                onFocus={() => setShowSuggestions(true)}
                onBlur={() => setTimeout(() => setShowSuggestions(false), 100)}
                ref={inputRef}
              />
              <FiSearch size={20} className='absolute left-2 top-1/2 -translate-y-1/2 text-black/70 dark:text-white/70' />
              {showSuggestions && filteredSuggestions.length > 0 && (
                <div className="absolute left-0 right-0 mt-1 bg-white dark:bg-secondaryDark rounded-md shadow-xl z-50 max-h-60 overflow-y-auto">
                  {filteredSuggestions.map((suggestion, idx) => (
                    <div
                      key={idx}
                      className="px-4 py-2 cursor-pointer hover:bg-gray-100 dark:hover:bg-dark text-black dark:text-white text-sm"
                      onMouseDown={() => {
                        setSearch(suggestion)
                        setShowSuggestions(false)
                        inputRef.current?.blur()
                      }}
                    >
                      {suggestion}
                    </div>
                  ))}
                </div>
              )}
              <button
                onClick={() => setCurrentPage(1)}
                className="bg-yellowcolor hover:bg-yellowcolor/80 text-black px-6 flex items-center justify-center py-2 rounded-md ml-2"
              >
                Search
              </button>
            </div>
            {/* Group Type Filter */}
            <div className="w-full md:w-1/6">
              <select
                className="w-full bg-white dark:bg-dark border dark:border-border-dark dark:text-white rounded-md py-2 font-light px-3"
                value={groupType}
                onChange={e => {
                  setGroupType(e.target.value)
                  setCurrentPage(1)
                }}
              >
                <option value="">Group Type</option>
                {GROUP_TYPES.filter(gt => gt.value).map(gt => (
                  <option key={gt.value} value={gt.value}>{gt.label}</option>
                ))}
              </select>
            </div>
            {/* Date Range Filter */}
            <div className="w-full md:w-1/4  flex items-end justify-end">
              <DateRangePickerDropdown
                range={dateRange.start && dateRange.end ? [{ startDate: dateRange.start, endDate: dateRange.end, key: 'selection' }] : undefined}
                onChange={(rangeArr) => {
                  setDateRange({ start: rangeArr[0].startDate, end: rangeArr[0].endDate })
                  setCurrentPage(1)
                }}
              />
            </div>
            {/* Reset Button */}
            {(search?.length > 0 || (dateRange?.start && dateRange?.end) || groupType) && <div className="flex items-end -mt-14 md:mt-0">
              <button
                onClick={resetFilters}
                className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-md text-sm"
              >
                Reset
              </button>
            </div>}
          </div>
          {/* Table and Pagination */}
          {loading ? (
            <div className="h-96 grid items-center justify-center">
              <Spinner />
            </div>
          ) : (
              <>
              <div className="my-5">
                  {filteredGroups.length > 0 ? (
                  <div className="my-5">
                      <div className="flex flex-col">
                        <div className="-m-1.5 overflow-x-auto">
                          <div className="p-1.5 min-w-full inline-block align-middle">
                            <div className="bg-white dark:bg-dark border dark:border-border-dark dark:text-white bg-clip-border rounded-lg shadow-sm overflow-hidden ">
                              <table className="min-w-full divide-y divide-gray-200 dark:divide-border-dark">
                                <thead className="bg-transparent dark:bg-secondaryDark">
                                <tr>
                                    <th className="px-6 py-3 text-start text-sm font-light text-gray-500 capitalize">Group ID</th>
                                    <th className="px-6 py-3 text-start text-sm font-light text-gray-500 capitalize">Group Name</th>
                                    <th className="px-6 py-3 text-start text-sm font-light text-gray-500 capitalize">Created on</th>
                                    <th className="px-6 py-3 text-start text-sm font-light text-gray-500 capitalize">Group Type</th>
                                    <th className="px-6 py-3 text-start text-sm font-light text-gray-500 capitalize">Number Of Members</th>
                                    <th className="px-6 py-3 text-start text-sm font-light text-gray-500 capitalize">Description</th>
                                    <th className="px-6 py-3 text-end text-sm font-light text-gray-500 capitalize">Actions</th>
                                </tr>
                              </thead>
                                <tbody className="divide-y bg-white divide-gray-200 dark:bg-dark dark:divide-border-dark">
                                  {paginatedGroups.map((data, index) => (
                                  <tr key={index} className="text-[13px]">
                                      <td className="px-6 py-4 whitespace-nowrap">{data?.group_id}</td>
                                      <td className="px-6 py-4 whitespace-nowrap">{data?.group_name}</td>
                                      <td className="px-6 py-4 whitespace-nowrap">{data?.created_at ? new Date(data.created_at).toLocaleDateString('en-US', { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric' }) : '-'}</td>
                                      <td className="px-6 py-4 whitespace-nowrap font-light">
                                        <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-semibold ${data?.group_type === 'farmers' ? 'bg-green-50 text-green-700' : data?.group_type === 'processors' ? 'bg-yellowcolor/20 text-yellowcolor' : 'bg-blue-50 text-blue-700'}`}>{data?.group_type ? data?.group_type.charAt(0).toUpperCase() + data?.group_type.slice(1) : ''}</span>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="flex flex-row items-center gap-2">
                                          <span>{data?.total_members} members</span>

                                      </div>
                                    </td>
                                      <td className="px-6 py-4 capitalize whitespace-nowrap font-light">{truncate(data?.description, 40)}</td>
                                    <td className="px-6 py-4 whitespace-nowrap text-end font-light">
                                      <div className="flex flex-row items-center gap-4">
                                        <Tooltip text="View" position="top">
                                            <button onClick={() => handleRowClick(data)}>
                                              <FiEye size={16} />
                                            </button>
                                          </Tooltip>
                                          <Tooltip text="Edit" position="top">
                                            <button onClick={() => history.push("/admin/edit-group", {
                                              state: {
                                                id: data?.id,
                                                ...data
                                              }
                                            })}>
                                              <RxPencil1 size={16} />
                                          </button>
                                        </Tooltip>
                                        <Tooltip text="Delete" position="top">
                                            <button onClick={() => { setSelected(data.id); setShowConfirmModal(true) }}>
                                            <svg width="21" height="20" viewBox="0 0 21 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                              <path d="M18 4.98307C15.225 4.70807 12.4333 4.56641 9.65 4.56641C8 4.56641 6.35 4.64974 4.7 4.81641L3 4.98307" stroke="#FE2B2B" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                              <path d="M7.58398 4.14102L7.76732 3.04935C7.90065 2.25768 8.00065 1.66602 9.40898 1.66602H11.5923C13.0007 1.66602 13.109 2.29102 13.234 3.05768L13.4173 4.14102" stroke="#FE2B2B" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                              <path d="M16.2077 7.61523L15.666 16.0069C15.5744 17.3152 15.4993 18.3319 13.1743 18.3319H7.82435C5.49935 18.3319 5.42435 17.3152 5.33268 16.0069L4.79102 7.61523" stroke="#FE2B2B" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                              <path d="M9.10742 13.75H11.8824" stroke="#FE2B2B" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                              <path d="M8.41602 10.416H12.5827" stroke="#FE2B2B" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                            </svg>
                                          </button>
                                        </Tooltip>
                                      </div>
                                    </td>
                                  </tr>
                                ))}
                                </tbody>
                            </table>
                              {/* Pagination */}
                              <Pagination
                                currentPage={currentPage}
                                totalPages={totalPages}
                                onPageChange={handlePageChange}
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <EmptyGroup handleNewGroup={handleNewGroup} />
                  )}
              </div>
              </>
          )}
        </div>
      </div>
    </>
  )
}

export default Index
